import {
  getCustomerInvite,
  PartyAccountBook,
  SortDirection,
  useAppSelector,
  useLazyGetCustomerLedgerQuery,
  useLazyGetLedgerTrailBalanceSummaryQuery,
} from '@zonofi/zono-money-store';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlDateRange } from '@zonofi/seller-hub/hooks/use-url-date-range';
import moment from 'moment';

export const useLedgerScreen = () => {
  const inviteData = useAppSelector(getCustomerInvite);
  const lastItemRef = useRef<null | HTMLDivElement>(null);
  const [ledgerList, setLedgerList] = useState<PartyAccountBook[]>([]);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const [error, setError] = useState({
    show: false,
    code: null,
    message: null,
  });
  const [showToastError, setShowToastError] = useState({
    show: false,
    message: '',
  });
  const [filterPage, setFilterPage] = useState<string>('');
  const { dateRangeFromUrl: appliedDateRange } = useUrlDateRange();

  const [ledgerListAPI, ledgerListAPIResponse] =
    useLazyGetCustomerLedgerQuery();
  const [getLedgerSummaryAPI, getLedgerSummaryAPIResponse] =
    useLazyGetLedgerTrailBalanceSummaryQuery();

  const isLedgerLoading = useMemo(() => {
    return ledgerListAPIResponse?.isLoading;
  }, [ledgerListAPIResponse]);

  const isEndOfList = useMemo(() => {
    return (
      ledgerListAPIResponse?.data?.endRecord !==
        ledgerListAPIResponse?.data?.totalPartyAccountBook &&
      isPageLoading &&
      pageNo !== 1
    );
  }, [
    ledgerListAPIResponse?.data?.endRecord,
    ledgerListAPIResponse?.data?.totalPartyAccountBook,
    isPageLoading,
    pageNo,
  ]);

  const openFiltersDrawer = (filterPage: string) => {
    setFilterPage(filterPage);
  };

  const onScroll = useCallback(() => {
    console.log('running onScroll...');
    if (lastItemRef && lastItemRef?.current) {
      const elementInViewport = (element: HTMLDivElement | null) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();

        return rect.bottom - 20 <= window.innerHeight;
      };
      const reachedBottom = elementInViewport(lastItemRef?.current);
      console.log('reached bottom', reachedBottom);
      if (
        reachedBottom &&
        !ledgerListAPIResponse?.isLoading &&
        !ledgerListAPIResponse?.isFetching &&
        !isPageLoading
      ) {
        console.log('reached');
        setPageNo(pageNo + 1);
        setIsPageLoading(true);
      }
    }
  }, [
    lastItemRef,
    ledgerListAPIResponse?.isLoading,
    ledgerListAPIResponse?.isFetching,
    isPageLoading,
    pageNo,
  ]);

  const getLedgerList = (isPageChange?: boolean) => {
    setError({
      show: false,
      code: null,
      message: '',
    });
    if (
      isPageChange &&
      ledgerListAPIResponse?.isSuccess &&
      ledgerListAPIResponse?.data?.endRecord >=
        ledgerListAPIResponse?.data?.totalPartyAccountBook
    ) {
      return;
    }
    ledgerListAPI({
      sellerWorkspaceId: inviteData?.seller?.workspaceId,
      inviteId: inviteData?.inviteId,
      startDate: `${moment(appliedDateRange?.startDate, 'DD/MM/YYYY').format(
        'YYYY-MM-DD'
      )}`,
      endDate: `${moment(appliedDateRange?.endDate, 'DD/MM/YYYY').format(
        'YYYY-MM-DD'
      )}`,
      pageSize: 20,
      pageNo: pageNo,
      sortDirection: SortDirection.Descending,
    });
    getLedgerSummaryAPI({
      sellerWorkspaceId: inviteData?.seller?.workspaceId,
      inviteId: inviteData?.inviteId,
      startDate: `${moment(appliedDateRange?.startDate, 'DD/MM/YYYY').format(
        'YYYY-MM-DD'
      )}`,
      endDate: `${moment(appliedDateRange?.endDate, 'DD/MM/YYYY').format(
        'YYYY-MM-DD'
      )}`,
    });
  };

  useEffect(() => {
    setPageNo(1);
    setIsEmpty(false);
    setLedgerList([]);
    getLedgerList();
  }, [
    appliedDateRange.startDate.toString(),
    appliedDateRange.endDate.toString(),
  ]);

  useEffect(() => {
    getLedgerList(true);
  }, [pageNo]);

  useEffect(() => {
    if (ledgerListAPIResponse?.isSuccess) {
      let ledgerToAdd = ledgerListAPIResponse?.data?.partyAccountBook;
      if (ledgerToAdd?.length === 0) return setIsEmpty(true);
      setIsEmpty(false);
      if (ledgerListAPIResponse?.originalArgs?.pageNo !== 1) {
        setLedgerList((prevState) => {
          return [...prevState, ...ledgerToAdd];
        });
      } else {
        setLedgerList((prevState) => {
          return [...ledgerToAdd];
        });
      }
    } else if (ledgerListAPIResponse?.isError) {
      console.log('error: ', ledgerListAPIResponse?.error);
      if (ledgerListAPIResponse?.error) {
        setIsEmpty(false);
        setLedgerList([]);
        setError({
          show: true,
          code: (ledgerListAPIResponse?.error as any)?.status ?? 500,
          message:
            (ledgerListAPIResponse?.error as any)?.data?.message?.message ??
            'Something went wrong!',
        });
      }
    }
    setIsPageLoading(false);
  }, [ledgerListAPIResponse]);

  return {
    isPageLoading,
    setIsPageLoading,
    pageNo,
    setPageNo,
    isEmpty,
    setIsEmpty,
    error,
    setError,
    showToastError,
    setShowToastError,
    isLedgerLoading,
    ledgerList,
    onScroll,
    lastItemRef,
    isEndOfList,
    ledgerListAPIResponse,
    getLedgerSummaryAPIResponse,
    filterPage,
    openFiltersDrawer,
    setFilterPage,
  };
};
